import { Phase, Player } from '../../types'
import {
  ActionType,
  EnginePhase,
  isOpeningTurn,
  nextEnginePhase,
  type MatchState,
  type PlayerAction,
  type Seat,
} from '@shared/index'

/** Phase/turn shown on the HUD while Phaser is still animating an older board. */
export interface PhaseHud {
  phase: EnginePhase
  turnPlayer: Seat
  turnCount: number
  absoluteTurnCount: number
}

// Adapts the authoritative network MatchState to the shape the existing Vue
// components (PhaseControl, TurnInfo) expect, so the polished HUD can be reused
// without the components knowing anything about the network layer.
//
// Phase/Player enum string values intentionally match EnginePhase, so the mapping
// is a value-preserving cast. "You" is always rendered as PLAYER_1 from the
// viewer's perspective regardless of the actual seat index.
/** Extra click while the board still shows the phase we just left. */
const PHASE_JUMP_GAP_MS = 450

export class NetworkPhaseAdapter {
  /** from-phase of the last ADVANCE we sent, so a double-click cannot send another. */
  private phaseJump: { from: EnginePhase; at: number } | null = null

  constructor(
    private readonly getState: () => MatchState | null,
    private readonly getMySeat: () => Seat,
    private readonly send: (action: PlayerAction) => void,
    private readonly getHud: () => PhaseHud | null = () => null,
    private readonly getBusy: () => boolean = () => false,
  ) {}

  getCurrentPhase(): Phase {
    const phase = this.getHud()?.phase ?? this.getState()?.phase ?? EnginePhase.DRAW
    return phase as unknown as Phase
  }

  getCurrentPlayer(): Player {
    const tp = this.getHud()?.turnPlayer ?? this.getState()?.turnPlayer
    if (tp !== 0 && tp !== 1) return Player.PLAYER_1
    return tp === this.getMySeat() ? Player.PLAYER_1 : Player.PLAYER_2
  }

  // PhaseControl treats "turn 1" as the only turn without a Battle Phase. That must
  // only apply to the very first turn of the game, so we expose absoluteTurnCount
  // (which increments every turn) instead of turnCount (which only bumps on seat 0).
  getTurnCount(): number {
    return this.getHud()?.absoluteTurnCount ?? this.getState()?.absoluteTurnCount ?? 1
  }

  isBattleInProgress(): boolean {
    return this.getBusy()
  }

  isGameEnded(): boolean {
    return this.getState()?.status === 'finished'
  }

  /** Soul Exchange: this turn has no Battle Phase, so Main 1 leads to End. */
  skipsBattlePhase(): boolean {
    const s = this.getState()
    if (!s || s.turnPlayer !== this.getMySeat()) return false
    return s.zones[this.getMySeat()].skipBattlePhase
  }

  /**
   * A second ADVANCE from the same phase, before the strip catches up, lands
   * one phase past the button the player clicked.
   */
  private claimPhaseJump(from: EnginePhase): boolean {
    const now = Date.now()
    const prev = this.phaseJump
    if (prev && prev.from === from && now - prev.at < PHASE_JUMP_GAP_MS) return false
    this.phaseJump = { from, at: now }
    return true
  }

  /**
   * Walk the engine's phase chain, or skip straight to End Phase (Battle and
   * Main 2 are optional). Clicking EP also ends the turn.
   */
  async setPhase(target: Phase): Promise<void> {
    const s = this.getState()
    // A trap question with no prompt still blocks Advance. The phase button
    // is that Pass: answer the window, then the player can move the phase.
    const owed = s?.pendingPriority
    if (owed && owed.responder === this.getMySeat()) {
      if (this.getBusy()) return
      this.send({
        type: ActionType.RESPOND_PRIORITY,
        requestId: owed.requestId,
        cardUid: null,
      })
      return
    }
    if (this.getBusy()) return
    if (!s || s.turnPlayer !== this.getMySeat()) return
    const targetEngine = target as unknown as EnginePhase

    if (targetEngine === EnginePhase.END) {
      if (!this.claimPhaseJump(s.phase)) return
      if (s.phase !== EnginePhase.END) {
        this.send({ type: ActionType.ADVANCE_PHASE, to: EnginePhase.END })
      }
      this.send({ type: ActionType.ADVANCE_PHASE })
      return
    }

    let cur = s.phase
    let steps = 0
    while (cur !== targetEngine && cur !== EnginePhase.END && steps < 6) {
      cur = nextEnginePhase(cur, isOpeningTurn(s))
      steps += 1
    }
    if (cur !== targetEngine || steps === 0) return
    if (!this.claimPhaseJump(s.phase)) return

    for (let i = 0; i < steps; i++) {
      this.send({ type: ActionType.ADVANCE_PHASE })
    }
  }
}
